import React, { useMemo, useState } from 'react';
import {
  Building2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { defaultProviderForm, defaultGrantForm, isRecordFromProvider, formatProviderAddress } from '../components/providers/providerUtils';
import ProviderWorkspace from '../components/providers/ProviderWorkspace';
import AddProviderModal from '../components/providers/AddProviderModal';
import GrantPermissionModal from '../components/providers/GrantPermissionModal';

export default function Providers({
  records,
  providers,
  permissions,
  onAddProvider,
  onUpdateProvider,
  onGrantPermission
}) {
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [showGrantPermission, setShowGrantPermission] = useState(false);
  const [providerForm, setProviderForm] = useState(defaultProviderForm);
  const [grantForm, setGrantForm] = useState(defaultGrantForm);
  const [isSavingProvider, setIsSavingProvider] = useState(false);
  const [isSavingPermission, setIsSavingPermission] = useState(false);
  const [providerError, setProviderError] = useState('');
  const [grantError, setGrantError] = useState('');
  const [expandedProviderId, setExpandedProviderId] = useState('');

  const providerMap = useMemo(
    () => new Map(providers.map((provider) => [provider.id, provider])),
    [providers]
  );

  const permissionsByProvider = useMemo(() => {
    const grouped = new Map();
    for (const permission of permissions) {
      const key = permission.provider?.id || 'unassigned';
      const current = grouped.get(key) || [];
      current.push(permission);
      grouped.set(key, current);
    }
    return grouped;
  }, [permissions]);

  const recordsByProvider = useMemo(() => {
    const grouped = new Map();
    for (const provider of providers) {
      grouped.set(provider.id, records.filter((record) => isRecordFromProvider(record, provider)));
    }
    return grouped;
  }, [providers, records]);

  /** Purpose: Opens permission modal pre-selecting provider. Inputs: providerId = ''. Output: none. */
  const openGrantModal = (providerId = '') => {
    setGrantForm({
      ...defaultGrantForm,
      providerId
    });
    setGrantError('');
    setShowGrantPermission(true);
  };

  /** Purpose: Handles add-provider form submission. Inputs: event. Output: Promise with operation result. */
  const handleProviderSubmit = async (event) => {
    event.preventDefault();
    if (!providerForm.name || !providerForm.organization) {
      setProviderError('Provider name and organization are required.');
      return;
    }

    setIsSavingProvider(true);
    setProviderError('');
    try {
      const created = await onAddProvider(providerForm);
      setProviderForm(defaultProviderForm);
      setShowAddProvider(false);
      setExpandedProviderId(created.id);
    } catch (submitError) {
      setProviderError(submitError.message || 'Failed to add provider.');
    } finally {
      setIsSavingProvider(false);
    }
  };

  /** Purpose: Handles grant-permission form submission. Inputs: event. Output: Promise with operation result. */
  const handleGrantSubmit = async (event) => {
    event.preventDefault();
    if (!grantForm.providerId || !grantForm.purpose || !grantForm.expiryDate) {
      setGrantError('Select a provider, purpose, and expiry date.');
      return;
    }
    if (!grantForm.allRecords && grantForm.recordIds.length === 0) {
      setGrantError('Select one or more records, or choose all records.');
      return;
    }

    setIsSavingPermission(true);
    setGrantError('');
    try {
      const provider = providerMap.get(grantForm.providerId);
      await onGrantPermission({
        providerId: grantForm.providerId,
        grantedTo: provider?.name,
        organization: provider?.organization,
        purpose: grantForm.purpose,
        expiryDate: grantForm.expiryDate,
        accessLevel: 'READ',
        allRecords: grantForm.allRecords,
        recordIds: grantForm.allRecords ? [] : grantForm.recordIds
      });
      setGrantForm(defaultGrantForm);
      setShowGrantPermission(false);
    } catch (submitError) {
      setGrantError(submitError.message || 'Failed to grant permission.');
    } finally {
      setIsSavingPermission(false);
    }
  };

  /** Purpose: Toggles record selection for grant-permission modal. Inputs: recordId. Output: none. */
  const toggleGrantRecord = (recordId) => {
    setGrantForm((previous) => ({
      ...previous,
      recordIds: previous.recordIds.includes(recordId)
        ? previous.recordIds.filter((id) => id !== recordId)
        : [...previous.recordIds, recordId]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Providers</h2>
          <p className="text-slate-600 mt-1">
            Click a provider card to edit details, review originating records, send records, or create provider-to-provider requests.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button
            onClick={() => {
              setProviderError('');
              setShowAddProvider(true);
            }}
            className="w-full sm:w-auto px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50"
          >
            Add Provider
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {providers.map((provider) => {
          const linkedPermissions = permissionsByProvider.get(provider.id) || [];
          const activeCount = linkedPermissions.filter((permission) => permission.status === 'active').length;
          const providerRecords = recordsByProvider.get(provider.id) || [];
          const isExpanded = expandedProviderId === provider.id;

          return (
            <div key={provider.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <button
                type="button"
                onClick={() => setExpandedProviderId((current) => (current === provider.id ? '' : provider.id))}
                className="w-full text-left"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <p className="text-base font-semibold text-slate-900">
                      {provider.name}
                      {provider.title ? `, ${provider.title}` : ''}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">{provider.organization}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {provider.email || 'No email'}{provider.phone ? ` • ${provider.phone}` : ''}
                    </p>
                    {formatProviderAddress(provider) && (
                      <p className="text-xs text-slate-500 mt-1">{formatProviderAddress(provider)}</p>
                    )}
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs text-slate-500">Active Permissions</p>
                    <p className="text-lg font-semibold text-slate-900">{activeCount}</p>
                    <p className="text-xs text-slate-500 mt-1">{providerRecords.length} originating record(s)</p>
                    <p className="mt-2 text-slate-500 inline-flex items-center gap-1 text-xs">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      {isExpanded ? 'Collapse' : 'Open'}
                    </p>
                  </div>
                </div>
              </button>

              {isExpanded && (
                <ProviderWorkspace
                  provider={provider}
                  providerRecords={providerRecords}
                  providerOptions={providers.filter((item) => item.id !== provider.id)}
                  allPermissions={permissions}
                  linkedPermissions={linkedPermissions}
                  onAddProvider={onAddProvider}
                  onUpdateProvider={onUpdateProvider}
                  onGrantPermission={onGrantPermission}
                  onOpenGrantModal={openGrantModal}
                />
              )}
            </div>
          );
        })}

        {providers.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
            <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-600">No providers added yet.</p>
            <p className="text-sm text-slate-500 mt-1">Start with provider details, then grant permissions when you are ready.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <button
                onClick={() => {
                  setProviderError('');
                  setShowAddProvider(true);
                }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50"
              >
                Add Provider
              </button>
            </div>
          </div>
        )}
      </div>

      <AddProviderModal
        open={showAddProvider}
        onClose={() => { setShowAddProvider(false); setProviderError(''); }}
        providerForm={providerForm}
        setProviderForm={setProviderForm}
        providerError={providerError}
        isSavingProvider={isSavingProvider}
        onSubmit={handleProviderSubmit}
      />

      <GrantPermissionModal
        open={showGrantPermission}
        onClose={() => { setShowGrantPermission(false); setGrantError(''); }}
        grantForm={grantForm}
        setGrantForm={setGrantForm}
        grantError={grantError}
        isSavingPermission={isSavingPermission}
        onSubmit={handleGrantSubmit}
        providers={providers}
        records={records}
        onToggleGrantRecord={toggleGrantRecord}
      />
    </div>
  );
}
